<template>
  <div class="documentation" redesign>
    <!-- Header -->
    <NomineeProgressBar
      stepName="Documentation"
      :stepNumber=2
      :numberOfSteps=2
    />

    <div class="form-container documentation-form">
      <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
      <template v-if="!loading">
        <!-- Information -->
        <div class="form-header">
          <span v-translate>
            In order to be eligible for your this month’s stipend, you need to provide proof of payment for housing this month (for example: a rent receipt or lease in your name), as well as proof of your monthly income of at least $2,000 before taxes and deductions (for example: pay stubs or earnings statements.) You need to document at least 4 weeks of last month’s income in order for your monthly stipend request to be approved.
          </span>
        </div>

        <!-- Sample documentation -->
        <div class="document-information-container">
          <h4 class="form-field-header" v-translate>Your documents need to show:</h4>
          <div class="documentation-row-wrapper">
            <div class="form-field-container sample-document-container">
              <img
                :src="require('@/assets/images/Sample Document - Document your emergency.svg')"
              />
            </div>
            <div class="form-field-container">
              <ol class="document-requirements-list">
                <li style="--n: '1'">
                  <span v-translate>The entire document, including headers and footers</span>
                </li>
                <li style="--n: '2'">
                  <span v-translate>
                    Name and information of the entity (for example: biller or landlord) that issued
                    the document
                  </span>
                </li>
                <li style="--n: '3'"><span v-translate>Document date</span></li>
                <li style="--n: '4'">
                  <span v-translate>Information that describes your payment for housing, as well as proof of income of at least $2,000 before taxes and deductions.</span>
                </li>
                <li style="--n: '5'"><span v-translate>Your name</span></li>
                <li style="--n: '6'">
                  <span v-translate>
                    Covered social security numbers, bank account numbers, or passwords
                  </span>
                </li>
                <li style="--n: '7'">
                  <span v-translate>Readable details, not blurry or cut off</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div class="stipend-request-uploads">
          <DocumentUpload
            :stipend="stipendRequest"
            :documentList.sync="documents"
            :loading="documentsLoading"
            docDetailType="STIPEND"
            @documents-changed="onDocumentsChanged($event, stipendRequest.id)"
            @delete-document="onDeleteDocument($event, stipendRequest.id)"
            @document-detail-type-selected="updateDocumentsDetailType($event, stipendRequest.id)"
            :darkText="true"
          />
        </div>

        <div v-if="error" class="error-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/error-icon.png" />
          </div>
          <div class="tooltip">
            <span class="tooltip-text" v-translate>
              You must upload at least one document to continue.
            </span>
          </div>
        </div>

        <SubmitButton
          class="form-button-160"
          :onContinue="handleSubmit"
          :buttonText="buttonText"
          :staticWidth="true"
          :darkText="true"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner } from '@/components'
import NomineeProgressBar from '@/components/stepper/NomineeProgressBar'
import DocumentUpload from '@/components/requests/DocumentUpload'
import Document from '@/services/documents'
import SubmitButton from '@/components/requests/SubmitButton'

import NomineesApi from '@/services/nominees'

export default {
  name: 'StipendDocumentation',
  components: {
    LoadSpinner,
    NomineeProgressBar,
    SubmitButton,
    DocumentUpload,
  },
  data() {
    return {
      loading: true,
      documentsLoading: false,
      stipendRequest: null,
      employeeCode: null,
      documents: [],
      killList: [],
      error: false,
    }
  },
  async created() {
    // Get our current request
    this.stipendRequest = this.currentRequest
    this.documents = this.stipendRequest.documentsRef || []

    this.loading = false
  },
  methods: {
    async handleSubmit() {
      if(!this.documents || !this.documents.length) {
        this.error = true
        return false
      }
      
      this.error = false

      await NomineesApi.submitStipendRequest(this.stipendRequest.id)

      return true
    },
    async onDocumentsChanged(documentList) {
      this.documentsLoading = true
      // Replace all documents for this particular event with the newest set
      this.documents = documentList

      if(this.documents && this.documents.length)
        this.error = false

      await this.updateDocuments()
      this.documentsLoading = false
    },
    async onDeleteDocument(docToDelete) {
      this.documentsLoading = true
      // If a document the user wants to delete has an ID, it means it exists in the database.
      // That means we need to keep track of it, and send a command to delete it later.
      if (docToDelete.id) {
        this.killList.push(docToDelete)
      }
      this.documents = this.documents.filter(doc => doc.id !== docToDelete.id)

      await this.updateDocuments()
      this.documentsLoading = false
    },
    async updateDocuments() {
      // Collect documents
      const documentList = this.documents

      // Loop through all documents from each event
      const promiseList = []
      documentList.forEach(doc => {
        const index = documentList.indexOf(doc)
        // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
        if (!doc.id) {
          promiseList.push(
            Document.api.create(doc).then(document => (documentList[index] = document)),
          )
        }
      })

      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)
      this.killList = []

      // Modify the request in state
      let stipendRequest = this.stipendRequest
      stipendRequest.documents = this.documents
      this.$store.dispatch('updateCurrentRequest', stipendRequest)
    },
    async updateDocumentsDetailType(docDetailType, stipendRequestId) {
      // Do nothing
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars', 'currentRequest']),
    buttonText() {
      return 'Submit'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';
@import '@/styles/grant-circles';

.documentation {
  display: flex;
  flex-direction: column;

  .documentation-form {
    @extend .constrain-tablet-and-up;

    @media (min-width: 1140px) {
      max-width: 1076px !important;
    }
  }

  @include tablet-and-up-screen {
    .documentation-row-wrapper {
      display: flex;
      flex-direction: row;

      .sample-document-container {
        min-width: 320px;
        margin: 0;
      }
    }
  }
}

.document-information-container {
  @extend .card-shape;
  background-color: rgba(0, 42, 139, 0.1);
  box-shadow: none;
  -webkit-box-shadow: none;
  margin-bottom: 2.5rem;

  .form-field-header {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .sample-document-container {
    text-align: center;
    margin-top: 1.5rem;
    margin-bottom: 1rem;

    img {
      width: 80%;
    }

    @include mobile-screen {
      img {
        max-width: 320px;
      }
    }

    @include tablet-and-up-screen {
      img {
        min-width: 266px;
      }
    }

    @include tablet-screen {
      text-align: left;
    }

    @include desktop-screen {
      margin-right: 2rem !important;
    }
  }

  .document-requirements-list {
    padding: 0;
    list-style: none;

    li {
      margin-left: 0;
      margin-bottom: 1rem;

      &:before {
        background-color: #212b36;
        border-radius: 50%;
        color: $white;
        content: var(--n);
        display: inline-block;
        height: 22px;
        text-align: center;
        width: 23px;
        vertical-align: top;
      }

      span {
        display: inline-block;
        padding-left: 0.75rem;
        width: 90%;
        font-size: 1.125rem;
        line-height: 1.5rem;
        font-weight: bold;
      }
    }
  }
}
</style>
